import React from 'react';
import { Link } from 'react-router-dom';

export const OverviewInstructions: React.FC = () => {
  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b-4 border-primary">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-primary mb-4 uppercase tracking-wide">
              📊 Sales Dashboard Pack
            </h1>
            <p className="text-xl text-text-secondary font-light">Overview & Instructions</p>
            <p className="text-sm text-text-muted mt-3">
              FY26 Performance Tracking System | Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Dashboard Objectives */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">🎯</span>
            <span>Dashboard Objectives</span>
          </h2>
          <div className="text-text-secondary text-lg mb-4">
            <p className="mb-4">
              <strong>What are we trying to achieve with these dashboards?</strong>
            </p>
            <p className="mb-6">
              This dashboard pack is designed to be a comprehensive sales performance management
              tool that serves multiple critical functions across our organization:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-3">🚀 Drive Sales Behaviors</h3>
              <p className="text-text-muted">
                Encourage proactive deal progression, accountability, and focus on high-value
                opportunities through transparent visibility of individual and team performance.
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-3">📈 Track Performance</h3>
              <p className="text-text-muted">
                Monitor real-time progress against our ambitious targets with clear visibility of
                pipeline health and deal confidence levels.
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-3">🤝 Support the Sales Team</h3>
              <p className="text-text-muted">
                Identify blockers, challenges, and support needs early through bi-weekly reviews
                and commentary tracking, enabling timely intervention and resource allocation.
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-3">🎉 Celebrate Achievements</h3>
              <p className="text-text-muted">
                Recognize wins, highlight top performers, and maintain team morale by showcasing
                successful deal closures and milestone achievements.
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-3">📊 Executive Tool</h3>
              <p className="text-text-muted">
                Provide leadership with a single source of truth for strategic decision-making,
                resource planning, and performance assessment across all revenue streams.
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-3">🔍 Transparency & Alignment</h3>
              <p className="text-text-muted">
                Create organizational alignment by ensuring everyone understands how their efforts
                contribute to our overall EBITDA targets and strategic goals.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-primary to-success text-black p-6 rounded-lg font-semibold text-lg">
            <strong>Ultimate Goal:</strong> Create a living, actionable tool that drives us toward
            our EBITDA targets through data-driven decision making, team accountability, and
            continuous performance optimization.
          </div>
        </section>

        {/* Review Cadence & Process */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">📅</span>
            <span>Review Cadence & Process</span>
          </h2>
          <div className="text-text-secondary text-lg mb-6">
            <p>
              <strong>How often will we review these dashboards?</strong>
            </p>
          </div>

          <div className="bg-gradient-to-r from-success to-green-600 text-white p-6 rounded-lg font-semibold text-lg mb-6">
            📅 <strong>Bi-Weekly Reviews</strong> - Every 2 weeks, the sales team will gather to
            review performance, update deal status, and identify actions.
          </div>

          <div className="mb-6">
            <p className="text-text-primary text-lg font-semibold mb-3">Review Meeting Structure:</p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 text-base">
              <li>
                <strong>Executive Summary Review (10 mins):</strong> Overall progress toward
                targets, key wins, and concerns
              </li>
              <li>
                <strong>Team Summary Review (10 mins):</strong> Individual performance against
                targets, status indicators, and pipeline health
              </li>
              <li>
                <strong>Individual Dashboard Deep-Dives (30 mins):</strong> Each team member
                presents their Top 3 deals, updates commentary, and discusses blockers
              </li>
              <li>
                <strong>Action Planning (10 mins):</strong> Identify support needs, resource
                allocation, and next steps
              </li>
            </ul>
          </div>

          <div>
            <p className="text-text-primary text-lg font-semibold mb-3">Between Reviews:</p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 text-base">
              <li>Team members update their deal commentary and notes as progress occurs</li>
              <li>Deal values, dates, and confidence levels are updated in the source Google Sheet</li>
              <li>Leadership can access dashboards at any time for real-time visibility</li>
            </ul>
          </div>
        </section>

        {/* Data Sources & Integration */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">🔗</span>
            <span>Data Sources & Integration</span>
          </h2>
          <div className="text-text-secondary text-lg mb-6">
            <p>
              <strong>Where is the data coming from?</strong>
            </p>
          </div>

          <div className="space-y-5 mb-6">
            <div className="bg-background p-6 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[150px] text-primary font-bold text-lg">CURRENT STATE</div>
              <div className="flex-1 text-text-secondary">
                <p className="mb-2">
                  <strong>Primary Data Source:</strong> Google Sheets spreadsheet
                </p>
                <p className="mb-2">
                  <strong>Manual Updates:</strong> Team commentary, weekly notes, and deal progress
                  updates are entered directly into individual dashboards
                </p>
                <p>
                  <strong>Update Frequency:</strong> Bi-weekly for deal values and dates; ongoing
                  for commentary
                </p>
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[150px] text-primary font-bold text-lg">FUTURE STATE</div>
              <div className="flex-1 text-text-secondary">
                <p className="mb-2">
                  <strong>Planned Integrations:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>
                    <strong>CRM System:</strong> Automatic sync of deal values, stages, close dates,
                    and contact information
                  </li>
                  <li>
                    <strong>Business Unit Dashboard:</strong> Real-time revenue data for baseline
                    business tracking
                  </li>
                  <li>
                    <strong>Automated Refresh:</strong> Daily data updates to minimize manual entry
                    and improve accuracy
                  </li>
                  <li>
                    <strong>API Connections:</strong> Seamless flow of data between systems
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-text-primary text-lg font-semibold mb-4">Data Flow Overview:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-md font-semibold text-primary mb-2">📊 Deal Data</h3>
              <p className="text-text-muted text-sm">
                Contract values, close dates, revenue start dates, confidence levels, and weightings
                sourced from Google Sheets (future: CRM System)
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-md font-semibold text-primary mb-2">💬 Commentary</h3>
              <p className="text-text-muted text-sm">
                Weekly progress notes, blocker descriptions, and action items entered manually by
                team members in editable dashboard fields
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-md font-semibold text-primary mb-2">📈 Baseline Revenue</h3>
              <p className="text-text-muted text-sm">
                Existing business performance tracked manually (future: automated from Business Unit
                Dashboard)
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-md font-semibold text-primary mb-2">🎯 Targets</h3>
              <p className="text-text-muted text-sm">
                Territory-based monthly targets and coverage goals set in individual dashboards and
                tracked against actuals
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-primary to-success text-black p-6 rounded-lg font-semibold mb-4">
            ⚠️ <strong>Important Note:</strong> All data changes should be made in the source
            Google Sheet first, then reflected in the dashboards during bi-weekly reviews. Manual
            commentary updates can be made directly in the dashboards at any time.
          </div>
        </section>

        {/* Hosting & Technical Infrastructure */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">🖥️</span>
            <span>Hosting & Technical Infrastructure</span>
          </h2>
          <div className="text-text-secondary text-lg mb-6">
            <p>
              <strong>Where will these dashboards be hosted?</strong>
            </p>
          </div>

          <div className="space-y-5 mb-6">
            <div className="bg-background p-6 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[150px] text-primary font-bold text-lg">CURRENT</div>
              <div className="flex-1 text-text-secondary">
                <p className="mb-2">
                  <strong>Cloud Hosting:</strong> Web-based application hosted on cloud infrastructure
                </p>
                <p className="mb-2">
                  <strong>Access:</strong> Secure web portal accessible via browser
                </p>
                <p>
                  <strong>Security:</strong> Authentication and authorization controls
                </p>
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[150px] text-primary font-bold text-lg">PLANNED</div>
              <div className="flex-1 text-text-secondary">
                <p className="mb-2">
                  <strong>Company Infrastructure:</strong> Dashboards will be integrated with internal
                  company systems
                </p>
                <p className="mb-2">
                  <strong>Benefits:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Centralized access for all team members</li>
                  <li>Automatic updates when data changes</li>
                  <li>Enhanced security with SSO integration</li>
                  <li>Version control and audit trails</li>
                  <li>Mobile-responsive access from any device</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-text-primary text-lg font-semibold mb-3">Technical Specifications:</p>
          <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 text-base">
            <li>
              <strong>Format:</strong> Modern web application with React and TypeScript
            </li>
            <li>
              <strong>Browser Compatibility:</strong> Chrome, Firefox, Safari, Edge (latest versions)
            </li>
            <li>
              <strong>Data Storage:</strong> Backend API with Google Sheets integration
            </li>
            <li>
              <strong>Responsive Design:</strong> Optimized for desktop viewing (1920x1080 recommended)
            </li>
          </ul>
        </section>

        {/* How to Use These Dashboards */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">📖</span>
            <span>How to Use These Dashboards</span>
          </h2>
          <p className="text-text-primary text-lg font-semibold mb-4">Navigation & Features:</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">🏠 Dashboard Home</strong>
              <p className="text-sm text-text-muted">
                Navigate to all dashboards and get overview metrics
              </p>
            </div>

            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">📖 Overview (This Page)</strong>
              <p className="text-sm text-text-muted">
                Read objectives, instructions, and understand the system
              </p>
            </div>

            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">📊 Executive Summary</strong>
              <p className="text-sm text-text-muted">
                High-level view of EBITDA paths with expandable deal details
              </p>
            </div>

            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">👥 Team Summary</strong>
              <p className="text-sm text-text-muted">
                All team members' performance at a glance with status indicators
              </p>
            </div>

            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">👤 Individual Dashboards</strong>
              <p className="text-sm text-text-muted">
                Detailed view for each team member with Top 3 deals and full pipeline
              </p>
            </div>

            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">🎨 Color Coding</strong>
              <p className="text-sm text-text-muted">
                GREEN = High confidence | ORANGE = Medium | RED = Low
              </p>
            </div>

            <div className="bg-background p-4 rounded-lg border-l-4 border-success">
              <strong className="text-success block mb-2">🚦 Status Indicators</strong>
              <p className="text-sm text-text-muted">
                ✓ On Track | ⚠ Needs Attention | ✗ Off Track
              </p>
            </div>
          </div>

          <p className="text-text-primary text-lg font-semibold mb-3 mt-6">Tips for Effective Use:</p>
          <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4 text-base">
            <li>
              <strong>Before Bi-Weekly Reviews:</strong> Update your deal commentary and review your
              Top 3 deals
            </li>
            <li>
              <strong>During Reviews:</strong> Use the dashboards as a presentation tool to walk
              through your pipeline
            </li>
            <li>
              <strong>After Reviews:</strong> Update any action items or changes discussed in the
              meeting
            </li>
            <li>
              <strong>For Executives:</strong> Use Executive Summary for quick status checks and Team
              Summary for performance overview
            </li>
            <li>
              <strong>For Sales Team:</strong> Focus on your individual dashboard and Top 3 deals for
              bi-weekly preparation
            </li>
          </ul>
        </section>

        {/* Dashboard Structure */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">🗂️</span>
            <span>Dashboard Structure</span>
          </h2>
          <p className="text-text-secondary text-lg mb-6">
            <strong>What's included in this pack?</strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-2">1. Overview & Instructions</h3>
              <p className="text-text-muted">
                This page - your guide to understanding and using the dashboard system
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-2">2. Executive Summary</h3>
              <p className="text-text-muted">
                Path to EBITDA targets with expandable deal categories and confidence breakdowns
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-2">3. Team Summary</h3>
              <p className="text-text-muted">
                All team members' performance, pipeline values, deal counts, and status indicators
              </p>
            </div>

            <div className="bg-background p-5 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-primary mb-2">4. Individual Dashboards</h3>
              <p className="text-text-muted">
                Dedicated pages for each sales representative with performance metrics, Top 3 deals,
                and detailed pipeline tables
              </p>
            </div>
          </div>
        </section>

        {/* Quick Start Guide */}
        <section className="bg-surface border-l-4 border-primary rounded-lg p-8 mb-8 shadow-lg">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center gap-4">
            <span className="text-4xl">🚀</span>
            <span>Quick Start Guide</span>
          </h2>
          <p className="text-text-secondary text-lg mb-6">
            <strong>New to the dashboards? Follow these steps:</strong>
          </p>

          <div className="space-y-5 mb-6">
            <div className="bg-background p-5 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[100px] text-primary font-bold text-lg">STEP 1</div>
              <div className="flex-1 text-text-secondary">
                <strong>Read this Overview page</strong> to understand objectives, review process,
                and data sources
              </div>
            </div>

            <div className="bg-background p-5 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[100px] text-primary font-bold text-lg">STEP 2</div>
              <div className="flex-1 text-text-secondary">
                <strong>Review the Executive Summary</strong> to see the big picture and understand
                our path to targets
              </div>
            </div>

            <div className="bg-background p-5 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[100px] text-primary font-bold text-lg">STEP 3</div>
              <div className="flex-1 text-text-secondary">
                <strong>Check the Team Summary</strong> to see where everyone stands and identify any
                concerns
              </div>
            </div>

            <div className="bg-background p-5 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[100px] text-primary font-bold text-lg">STEP 4</div>
              <div className="flex-1 text-text-secondary">
                <strong>Navigate to your Individual Dashboard</strong> and familiarize yourself with
                your Top 3 deals and pipeline
              </div>
            </div>

            <div className="bg-background p-5 rounded-lg border-l-4 border-primary flex gap-5">
              <div className="min-w-[100px] text-primary font-bold text-lg">STEP 5</div>
              <div className="flex-1 text-text-secondary">
                <strong>Update your commentary</strong> by adding your progress updates and preparing
                for bi-weekly reviews
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-primary to-success text-black p-6 rounded-lg font-semibold text-lg">
            💡 <strong>Pro Tip:</strong> Bookmark your individual dashboard for quick access before
            bi-weekly reviews. Spend 10 minutes before each meeting updating your notes and reviewing
            your Top 3 deals.
          </div>
        </section>

        {/* Navigate to Dashboards */}
        <section className="bg-surface border-l-4 border-success rounded-lg p-8 mb-8 shadow-lg text-center">
          <h2 className="text-3xl font-semibold text-primary mb-6 flex items-center justify-center gap-4">
            <span className="text-4xl">🧭</span>
            <span>Navigate to Dashboards</span>
          </h2>
          <p className="text-text-secondary text-lg mb-8">
            Ready to get started? Choose a dashboard below:
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/executive-summary"
              className="inline-block bg-primary hover:bg-primary-dark text-black font-bold py-4 px-8 rounded-lg text-lg transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              📊 Executive Summary
            </Link>
            <Link
              to="/team-summary"
              className="inline-block bg-primary hover:bg-primary-dark text-black font-bold py-4 px-8 rounded-lg text-lg transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              👥 Team Summary
            </Link>
            <Link
              to="/"
              className="inline-block bg-primary hover:bg-primary-dark text-black font-bold py-4 px-8 rounded-lg text-lg transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              🏠 Return to Home
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t-2 border-border bg-surface">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center">
          <p className="text-text-muted">
            <strong>Questions or Issues?</strong> Contact the Sales Operations team for support.
          </p>
          <p className="text-text-muted text-sm mt-2">
            Dashboard Pack Version 2.0 | {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </footer>
    </div>
  );
};
